import { Button } from "@/components/button/Button";
import LoginButton from "@/components/auth0/LoginButton";
import Profile from "@/components/auth0/Profile";
import iconNewChat from "@/assets/icon-new-chat.svg";
import Footer from "./footer";
import { useUser } from "@/providers/UserProvider";
import { useEffect, useState, useRef } from "react";
import type { Conversations } from "@/types/entities/conversations";
import { Trash } from "@phosphor-icons/react";
import { Modal } from "@/components/modal/Modal";

export default function Drawer({
  open,
  onOpenChange,
  newChatHandler,
  status,
  loadConvoHandler,
  currentConversationId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newChatHandler: () => void;
  status: string;
  loadConvoHandler: (convoId: number) => void;
  currentConversationId: number | null;
}) {
  const { userEmail, userName, isMobile, isAuthenticated, authToken } =
    useUser();
  const [conversations, setConversations] = useState<Conversations[]>([]);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const hasFetchedOnce = useRef(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTouchStart = (convoId: number) => {
    longPressTimer.current = setTimeout(() => {
      setPendingDeleteId(convoId);
    }, 1000);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // get conversation history if the user is logged in
  useEffect(() => {
    if (
      isAuthenticated &&
      userEmail &&
      authToken &&
      (!hasFetchedOnce.current || currentConversationId !== null)
    ) {
      hasFetchedOnce.current = true;
      fetch("/api/conversations", {
        headers: { Authorization: `Bearer ${authToken}` },
      })
        .then((res) => res.json())
        .then((data) => {
          if (!Array.isArray(data)) {
            console.error("Unexpected conversations data:", data);
            return;
          }
          setConversations(data as Conversations[]);
        })
        .catch(console.error);
    }
  }, [isAuthenticated, userEmail, authToken, currentConversationId]);

  const deleteConversation = (convoId: number) => {
    // 
    if (!authToken) return;
    fetch(`/api/conversations/${convoId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (res.ok) {
          setConversations((prev) =>
            prev.filter((convo) => convo.id !== convoId)
          );
        } else {
          console.error("Failed to delete conversation:", res.statusText);
        }
      })
      .catch(console.error);
  };

  return (
    <div
      className={`absolute inset-0 z-20 bg-black/50 transition-opacity ${
        open
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      }`}
      onClick={() => onOpenChange(false)}
    >
      <div
        className={`relative absolute top-0 left-0 h-full w-80 bg-white shadow-lg transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4">
          <Button
            variant="ghost"
            size="sm"
            className="btn--text rounded-full hover:!bg-transparent !px-0"
            onClick={() => {
              newChatHandler();
              onOpenChange(false);
            }}
            disabled={!(status === "ready")}
          >
            <img src={iconNewChat} alt="New Chat Icon" />
            <span className="text-base ml-1">New Chat</span>
          </Button>
          <h3 className="font-bold text-base mt-6">Chat History</h3>
          <ul className="list-none p-0">
            {conversations.length > 0 ? (
              conversations.map((convo) => (
                <li
                  key={convo.id}
                  className="text-base my-2 group flex items-center justify-between"
                  onTouchStart={() => handleTouchStart(convo.id)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={handleTouchEnd}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className="btn--text rounded-full hover:!bg-transparent !px-0"
                    onClick={() => {
                      loadConvoHandler(convo.id);
                      onOpenChange(false);
                    }}
                    disabled={!(status === "ready")}
                  >
                    <span className="text-base ml-0">
                      {convo.preview || "No preview available"}
                    </span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity !px-0 !text-gray-400 hover:!text-[#533366] hover:!bg-transparent"
                    aria-label="Delete conversation"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDeleteId(convo.id);
                    }}
                  >
                    <Trash size={16} />
                  </Button>
                </li>
              ))
            ) : (
              <li className="text-base my-2">No saved chats</li>
            )}
          </ul>
        </div>
        <Modal
          isOpen={pendingDeleteId !== null}
          onClose={() => setPendingDeleteId(null)}
          clickOutsideToClose
          className="p-6 max-w-sm"
        >
          <p className="text-lg font-medium mb-4">Delete this chat?</p>
          <div className="mt-6 flex gap-2 justify-start !px-0">
            <Button
              variant="ghost"
              size="base"
              className="!text-base !px-0 !text-white !bg-[#766699] !px-6 !py-3 !rounded-none !border-l-[7px] !border-l-[#443565] !border-t-0 !border-r-0 !border-b-0 hover:!bg-[#6a5c8a] !mr-4"
              onClick={() => {
                if (pendingDeleteId !== null) {
                  deleteConversation(pendingDeleteId);
                  setPendingDeleteId(null);
                }
              }}
            >
              Delete
            </Button>
            <Button
              variant="ghost"
              size="base"
              className="!text-base !px-0 hover:!bg-transparent hover:underline"
              onClick={() => setPendingDeleteId(null)}
            >
              Cancel
            </Button>
          </div>
        </Modal>
        <Footer>
          {(isAuthenticated || isMobile) && userEmail ? (
            <>
              {/* <LogoutButton />  */}
              <div className="flex items-center gap-2 ml-auto">
                <Profile userName={userName} className="text-base px-2" />
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="text-base">Log in to save your chats</span>
              </div>
              <LoginButton className="btn btn-ghost btn--text add-focus group interactive flex w-max shrink-0 items-center font-medium select-none add-size-sm rounded-full" />
            </>
          )}
        </Footer>
      </div>
    </div>
  );
}
